import type { ComponentProps, SyntheticEvent } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FILTER_CONTROL_CLASS,
  HIDDEN_UNTIL_FILTER_FIELD_ACTIVE_CLASS,
} from './advancedFilter.constants';

interface FilterPopoverTriggerProps extends ComponentProps<typeof Button> {
  text: string;
  hasValue: boolean;
  clearLabel: string;
  onClear: () => void;
  icon: LucideIcon;
}

export function FilterPopoverTrigger({
  text,
  hasValue,
  clearLabel,
  onClear,
  icon: Icon,
  className,
  ...buttonProps
}: FilterPopoverTriggerProps) {
  function handleClear(event: SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();
    onClear();
  }

  return (
    <Button
      {...buttonProps}
      variant="outline"
      size="sm"
      className={cn(
        'w-full justify-between px-3 text-sm font-normal hover:bg-card hover:text-foreground aria-expanded:bg-card aria-expanded:text-foreground',
        FILTER_CONTROL_CLASS,
        !hasValue && 'text-muted-foreground',
        className,
      )}
    >
      <span className={cn('truncate', !hasValue && HIDDEN_UNTIL_FILTER_FIELD_ACTIVE_CLASS)}>
        {text}
      </span>
      {hasValue ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={clearLabel}
          className="ml-2 inline-flex size-4 items-center justify-center rounded-sm hover:bg-muted"
          onClick={handleClear}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') handleClear(event);
          }}
        >
          <X className="size-3" />
        </span>
      ) : (
        <Icon className="ml-2 size-4 text-current opacity-60" />
      )}
    </Button>
  );
}
